"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";

export type Audience = "visitors" | "businesses";

const STORAGE_KEY = "va_audience";

interface AudienceContextValue {
  activeTab: Audience;
  setActiveTab: (tab: Audience) => void;
}

const AudienceContext = createContext<AudienceContextValue | null>(null);

interface AudienceProviderProps {
  children: React.ReactNode;
}

const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot(): Audience {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "businesses" ? "businesses" : "visitors";
}

function getServerSnapshot(): Audience {
  return "visitors";
}

export function AudienceProvider({ children }: AudienceProviderProps) {
  const activeTab = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const setActiveTab = useCallback((tab: Audience) => {
    localStorage.setItem(STORAGE_KEY, tab);
    listeners.forEach((l) => l());
  }, []);

  const value = useMemo(
    () => ({ activeTab, setActiveTab }),
    [activeTab, setActiveTab],
  );

  return (
    <AudienceContext.Provider value={value}>
      {children}
    </AudienceContext.Provider>
  );
}

export function useAudience() {
  const ctx = useContext(AudienceContext);
  if (!ctx) {
    throw new Error("useAudience must be used within AudienceProvider");
  }
  return ctx;
}
